/**
 * ClientEmailModal — Shared premium email preview modal for single-client
 * transactional sends (Introduction, Website Preview).
 *
 * Reuses the same "Campaign Studio" experience: preview iframe (desktop /
 * mobile), content editor, test send, recipient reveal, activity trail.
 *
 * Modes:
 *   - "intro"    → renders SHARED master intro campaign template. Content
 *                  edits persist to `PATCH /studio/templates/intro/intro`
 *                  so the Campaigns page and the Client Profile stay in sync.
 *   - "preview"  → renders per-send Website Preview email. Content is local
 *                  state; nothing persists between sends. Preview URL is
 *                  required and drives the CTA.
 */
import React, { useEffect, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { toast } from "sonner";
import {
  X, Send, RotateCcw, Save, Monitor, Smartphone, User, BarChart3,
  Eye, FileText, LinkIcon, Check,
} from "lucide-react";

const TABS_INTRO = [
  { key: "preview",  label: "Preview",   icon: Eye },
  { key: "content",  label: "Content",   icon: FileText },
  { key: "recipient", label: "Recipient", icon: User },
  { key: "activity", label: "Activity",  icon: BarChart3 },
];
const TABS_PREVIEW = [
  { key: "preview",  label: "Preview",   icon: Eye },
  { key: "content",  label: "Content",   icon: FileText },
  { key: "recipient", label: "Recipient", icon: User },
  { key: "activity", label: "Activity",  icon: BarChart3 },
];

const DEFAULT_PREVIEW_CONTENT = {
  subject:   "Your website preview is ready 🍊",
  preheader: "Take a look at what we've been building.",
  eyebrow:   "Your preview is ready",
  headline:  "TAKE A LOOK AT WHAT WE'VE BEEN BUILDING. 🍊",
  body:
    "We've been working on your new website/system and your latest preview is ready to view.\n\n" +
    "Take a look through it, click around and get a feel for how everything is coming together.\n\n" +
    "If there's anything you'd like adjusted, refined or given a little more juice, just let us know.\n\n" +
    "We're looking forward to hearing what you think.",
  cta_label: "VIEW YOUR PREVIEW",
};

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      {children}
    </label>
  );
}
function Input(props) {
  return (
    <input
      {...props}
      className={
        "w-full px-3 py-2 rounded-lg border border-[#EAEAEA] bg-white text-[#0A0A0A] " +
        "placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#FF6A00] text-[14px] " +
        (props.className || "")
      }
    />
  );
}
function TextArea(props) {
  return (
    <textarea
      {...props}
      className={
        "w-full px-3 py-2 rounded-lg border border-[#EAEAEA] bg-white text-[#0A0A0A] " +
        "placeholder:text-[#8A8A8A] focus:outline-none focus:border-[#FF6A00] text-[14px] " +
        "min-h-[140px] " + (props.className || "")
      }
    />
  );
}

export default function ClientEmailModal({ open, mode, businessId, onClose, onSent }) {
  const isIntro = mode === "intro";
  const TABS = isIntro ? TABS_INTRO : TABS_PREVIEW;

  const [tab, setTab] = useState("preview");
  const [device, setDevice] = useState("desktop");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewMeta, setPreviewMeta] = useState({ to_email: "", to_name: "" });
  const [content, setContent] = useState(isIntro ? {
    subject: "", preheader: "", eyebrow: "", headline: "", body: "", cta_label: "",
  } : { ...DEFAULT_PREVIEW_CONTENT });
  const [previewUrl, setPreviewUrl] = useState("");
  const [note, setNote] = useState("");
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [activity, setActivity] = useState(null);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setTab("preview"); setDevice("desktop"); setSending(false); setActivity(null);
    setPreviewHtml(""); setPreviewSubject("");
    if (isIntro) {
      // Load the shared master intro template
      hqApi.get("/control/studio/templates/intro/intro").then((r) => {
        const t = r.data.template || {};
        setContent({
          subject: t.subject || "", preheader: t.preheader || "",
          eyebrow: t.eyebrow || "", headline: t.headline || "",
          body: t.body || "", cta_label: t.cta_label || "",
        });
      }).catch(() => {});
    } else {
      setContent({ ...DEFAULT_PREVIEW_CONTENT });
      setPreviewUrl("");
      setNote("");
    }
  }, [open, mode, businessId, isIntro]);

  // Recompute preview whenever content / previewUrl change
  useEffect(() => {
    if (!open || !businessId) return;
    const url = isIntro
      ? `/control/clients/${businessId}/intro-email/preview`
      : `/control/clients/${businessId}/website-preview-email/preview`;
    const body = isIntro
      ? { content }
      : { content, preview_url: previewUrl || "https://preview.orangebysite.com/", note };
    hqApi.post(url, body).then((r) => {
      setPreviewHtml(r.data.html);
      setPreviewSubject(r.data.subject);
      setPreviewMeta({ to_email: r.data.to_email || "", to_name: r.data.to_name || "" });
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, businessId, mode, content, previewUrl, note]);

  // Load activity when tab opens
  useEffect(() => {
    if (!open || tab !== "activity" || !businessId) return;
    hqApi.get(`/control/clients/${businessId}`).then((r) => {
      const commType = isIntro ? "hq_introduction" : "hq_website_preview";
      const rows = (r.data.communications || []).filter((c) =>
        (c.comm_type || "").includes(commType.replace("hq_", ""))
      );
      setActivity(rows);
    }).catch(() => setActivity([]));
  }, [tab, open, businessId, isIntro]);

  if (!open) return null;

  const saveIntroTemplate = async () => {
    setSavingTpl(true);
    try {
      await hqApi.patch("/control/studio/templates/intro/intro", content);
      toast.success("Introduction template saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    setSavingTpl(false);
  };

  const resetIntroTemplate = async () => {
    if (!window.confirm("Reset the master introduction template to the OrangeBySite default?")) return;
    try {
      await hqApi.post("/control/studio/templates/intro/intro/reset");
      const r = await hqApi.get("/control/studio/templates/intro/intro");
      const t = r.data.template || {};
      setContent({
        subject: t.subject || "", preheader: t.preheader || "",
        eyebrow: t.eyebrow || "", headline: t.headline || "",
        body: t.body || "", cta_label: t.cta_label || "",
      });
      toast.success("Reset to OrangeBySite default");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };

  const resetPreviewContent = () => {
    setContent({ ...DEFAULT_PREVIEW_CONTENT }); setNote("");
    toast.success("Reset to default preview email");
  };

  const doTest = async () => {
    if (!testTo) return toast.error("Enter a test email");
    if (!isIntro && !previewUrl) return toast.error("Enter a preview URL first");
    if (!isIntro && !/^https?:\/\//.test(previewUrl)) return toast.error("Preview URL must start with http(s)://");
    try {
      const url = isIntro
        ? `/control/clients/${businessId}/intro-email/test`
        : `/control/clients/${businessId}/website-preview-email/test`;
      const body = isIntro
        ? { to_email: testTo, content }
        : { to_email: testTo, content, preview_url: previewUrl, note };
      const r = await hqApi.post(url, body);
      toast.success(`Test ${r.data.status} to ${r.data.to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test send failed"); }
  };

  const doSend = async () => {
    if (!previewMeta.to_email) return toast.error("This client has no email on file");
    if (!isIntro && !previewUrl) return toast.error("Enter a preview URL first");
    if (!isIntro && !/^https?:\/\//.test(previewUrl)) return toast.error("Preview URL must start with http(s)://");
    const label = isIntro ? "introduction" : "website preview";
    if (!window.confirm(`Send ${label} email to ${previewMeta.to_email}?`)) return;
    setSending(true);
    try {
      const url = isIntro
        ? `/control/clients/${businessId}/send-introduction`
        : `/control/clients/${businessId}/send-website-preview`;
      const body = isIntro
        ? { content }
        : { content, preview_url: previewUrl, note };
      const r = await hqApi.post(url, body);
      if (r.data.status === "sent") toast.success(`Sent to ${r.data.to}`);
      else if (r.data.status === "skipped") toast.message(`Skipped (provider not configured)`);
      else toast.error(`Send ${r.data.status}`);
      onSent && onSent(r.data);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Send failed"); }
    finally { setSending(false); }
  };

  const title = isIntro ? "Send introduction" : "Send website preview";
  const eyebrow = isIntro ? "Introductory campaign" : "Client email";
  const sendCta = isIntro ? "SEND INTRO" : "SEND PREVIEW";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-end lg:items-center lg:justify-center p-0 lg:p-6" data-testid="hq-client-email-modal">
      <div className="bg-white w-full lg:w-[95vw] lg:max-w-[1200px] lg:rounded-2xl overflow-hidden flex flex-col max-h-full">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{eyebrow}</div>
            <h2 className="mt-1 font-black text-xl md:text-2xl">{title}</h2>
            <p className="text-[12px] text-[#8A8A8A] mt-1">{previewSubject || content.subject}</p>
          </div>
          <button onClick={onClose} data-testid="hq-client-email-close" className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[#EAEAEA] px-3 overflow-x-auto" data-testid="hq-client-email-tabs">
          {TABS.map((t) => (
            <button
              key={t.key} data-testid={`hq-client-email-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-[11px] font-black tracking-[0.16em] uppercase whitespace-nowrap border-b-2 ${
                tab === t.key ? "border-[#FF6A00] text-[#0A0A0A]" : "border-transparent text-[#8A8A8A] hover:text-[#0A0A0A]"
              }`}
            >
              <t.icon size={12} className="inline mr-1"/> {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {tab === "preview" && (
            <div className="p-5 bg-[#F6F5F2]" data-testid="hq-client-email-preview">
              {!isIntro && (
                <div className="max-w-3xl mx-auto mb-4 p-3 bg-white rounded-xl border border-[#EAEAEA]">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1 flex items-center gap-1">
                    <LinkIcon size={11}/> Preview URL (CTA destination)
                  </div>
                  <Input
                    data-testid="hq-client-email-preview-url"
                    type="url" value={previewUrl}
                    onChange={(e) => setPreviewUrl(e.target.value)}
                    placeholder="https://preview.orangebysite.com/client-name"
                  />
                </div>
              )}
              <div className="flex gap-2 justify-end mb-3">
                <button data-testid="hq-client-email-device-desktop" onClick={() => setDevice("desktop")} className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase ${device === "desktop" ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA]"}`}><Monitor size={12} className="inline mr-1"/>Desktop</button>
                <button data-testid="hq-client-email-device-mobile" onClick={() => setDevice("mobile")} className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase ${device === "mobile" ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA]"}`}><Smartphone size={12} className="inline mr-1"/>Mobile</button>
              </div>
              <div className="mx-auto bg-white rounded-xl shadow-lg" style={{ width: device === "mobile" ? 375 : "100%", maxWidth: 780 }}>
                <iframe title="client-email-preview" srcDoc={previewHtml} style={{ width: "100%", height: device === "mobile" ? 700 : 900, border: 0, borderRadius: 12 }}/>
              </div>
            </div>
          )}

          {tab === "content" && (
            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="hq-client-email-content">
              {isIntro && (
                <div className="lg:col-span-2 p-3 rounded-xl bg-[#FFF7EE] border border-[#FCE1C7] text-[12px] text-[#4A4A4A]">
                  <strong>Shared master template.</strong> Any edits here are the SAME template used by Campaigns → Intro Campaign. Save to update everywhere; use Reset to restore the OrangeBySite default.
                </div>
              )}
              <Field label="Subject" testId="hq-client-email-field-subject">
                <Input value={content.subject} onChange={(e) => setContent({ ...content, subject: e.target.value })}/>
              </Field>
              <Field label="Preheader" testId="hq-client-email-field-preheader">
                <Input value={content.preheader} onChange={(e) => setContent({ ...content, preheader: e.target.value })}/>
              </Field>
              <Field label="Eyebrow" testId="hq-client-email-field-eyebrow">
                <Input value={content.eyebrow} onChange={(e) => setContent({ ...content, eyebrow: e.target.value })}/>
              </Field>
              <Field label="Headline" testId="hq-client-email-field-headline">
                <Input value={content.headline} onChange={(e) => setContent({ ...content, headline: e.target.value })}/>
              </Field>
              <div className="lg:col-span-2">
                <Field label="Body copy" testId="hq-client-email-field-body">
                  <TextArea value={content.body} onChange={(e) => setContent({ ...content, body: e.target.value })}/>
                </Field>
              </div>
              <Field label="CTA label" testId="hq-client-email-field-cta">
                <Input value={content.cta_label} onChange={(e) => setContent({ ...content, cta_label: e.target.value })}/>
              </Field>
              {!isIntro && (
                <Field label="Preview URL (CTA destination)" testId="hq-client-email-field-url">
                  <Input type="url" value={previewUrl} onChange={(e) => setPreviewUrl(e.target.value)}
                    placeholder="https://preview.orangebysite.com/…"/>
                </Field>
              )}
              {!isIntro && (
                <div className="lg:col-span-2">
                  <Field label="Optional note (shown above the CTA)" testId="hq-client-email-field-note">
                    <TextArea value={note} onChange={(e) => setNote(e.target.value)}/>
                  </Field>
                </div>
              )}
              <div className="lg:col-span-2 flex flex-wrap gap-2 justify-end pt-2 border-t border-[#EAEAEA]">
                <button
                  data-testid="hq-client-email-reset"
                  onClick={isIntro ? resetIntroTemplate : resetPreviewContent}
                  className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase hover:border-[#FF6A00]">
                  <RotateCcw size={12} className="inline mr-1"/> Reset to default
                </button>
                {isIntro && (
                  <button data-testid="hq-client-email-save-tpl" disabled={savingTpl} onClick={saveIntroTemplate}
                    className="px-5 py-2 rounded-full bg-[#0A0A0A] text-white text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
                    <Save size={12} className="inline mr-1"/> Save master template
                  </button>
                )}
              </div>
            </div>
          )}

          {tab === "recipient" && (
            <div className="p-5" data-testid="hq-client-email-recipient">
              <div className="max-w-lg p-4 rounded-xl border border-[#EAEAEA]">
                <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#FF6A00]">Sending to</div>
                <div className="mt-3 space-y-2">
                  <div><span className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A]">Name</span><div className="font-semibold">{previewMeta.to_name || "—"}</div></div>
                  <div><span className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A]">Email</span><div className="font-semibold" data-testid="hq-client-email-to-email">{previewMeta.to_email || <span className="text-[#C1350B]">No email on file</span>}</div></div>
                </div>
              </div>
              <p className="mt-4 text-[12px] text-[#4A4A4A] max-w-lg">
                Every send is logged in the client&#39;s Communications ledger with idempotency, so accidental duplicates are prevented.
              </p>
            </div>
          )}

          {tab === "activity" && (
            <div className="p-5" data-testid="hq-client-email-activity">
              {activity === null ? <div className="text-[#8A8A8A]">Loading…</div> : activity.length === 0 ? (
                <div className="text-[#8A8A8A]">No {isIntro ? "introduction" : "website preview"} emails sent to this client yet.</div>
              ) : (
                <ul className="space-y-2 text-sm max-h-[400px] overflow-auto">
                  {activity.map((c, i) => (
                    <li key={i} className="flex justify-between border-b border-[#EAEAEA] py-2">
                      <span>{c.subject || c.comm_type}</span>
                      <span className={`text-[10px] font-black tracking-[0.16em] uppercase ${c.status === "sent" ? "text-[#0FA958]" : c.status === "failed" ? "text-[#C1350B]" : "text-[#8A6D00]"}`}>
                        <Check size={11} className="inline mr-0.5"/>{c.status} · {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#EAEAEA] flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
          <div className="flex items-center gap-2">
            <Input data-testid="hq-client-email-test-input" placeholder="test@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="max-w-[240px]"/>
            <button data-testid="hq-client-email-send-test" onClick={doTest} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.16em] uppercase hover:border-[#FF6A00]">
              Send test
            </button>
          </div>
          <button
            data-testid="hq-client-email-send"
            disabled={sending || !previewMeta.to_email}
            onClick={doSend}
            className="px-5 py-2 rounded-full bg-[#FF6A00] text-black text-[10px] font-black tracking-[0.16em] uppercase disabled:opacity-50">
            <Send size={12} className="inline mr-1"/>
            {sending ? "Sending…" : `${sendCta} TO ${(previewMeta.to_name || "CLIENT").toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}
